// src/components/form/FormNavButtons.js

import { useTranslation } from "react-i18next";

/**
 * FormNavButtons
 * --------------
 * Re-usable navigation strip for any multi-step “wizard”.
 *
 * Props
 * -----
 * • `hasPrev` (bool)      – Show the “Previous” button when `true`.
 * • `onPrev` (fn)         – Callback fired on Previous click.
 * • `onNext` (fn)         – Callback fired on Next / Confirm click.
 * • `isLast` (bool)       – When `true` the primary button shows “Confirm”.
 * • `isSubmitting` (bool) – When `true` the primary button is disabled and
 *                           shows a “Saving…” label so the final step can’t be
 *                           submitted twice by an impatient double-click.
 *
 * Notes
 * • Purely presentational – no internal state or side-effects.
 * • Keeps button styling/markup in one place so different forms don’t
 *   duplicate the same flex/spacing logic.
 */
export default function FormNavButtons({
  hasPrev,
  onPrev,
  onNext,
  isLast,
  isSubmitting = false,
}) {
  const { t } = useTranslation();

  const primaryLabel = isSubmitting
    ? t("formNavButtons.saving", "Saving…")
    : isLast
      ? t("formNavButtons.confirm")
      : t("formNavButtons.next");

  return (
    <div className="flex justify-between mt-6">
      {hasPrev ? (
        <button
          type="button"
          onClick={onPrev}
          disabled={isSubmitting}
          className="outlined-button disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {t("formNavButtons.previous")}
        </button>
      ) : (
        <span /> /* keeps grid alignment when first step */
      )}

      <button
        type="button"
        onClick={onNext}
        disabled={isSubmitting}
        aria-busy={isSubmitting}
        className="primary-button disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {primaryLabel}
      </button>
    </div>
  );
}
